import {
  AiSummaryFieldModal,
  type AiSummaryFieldModalProps
} from '@databases/database-ai-fields';
import React, {
  createContext,
  type FunctionComponent,
  type ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState
} from 'react';

import { getExperimentValue } from '@canvas/client-feature-flags/experiments';

type AiSummaryFieldConfigModalProps = Omit<AiSummaryFieldModalProps, 'onClose'> & {
  onClose?: AiSummaryFieldModalProps['onClose'];
};

type DatabaseAiConfigModal = {
  type: 'ai-summary-field';
  props?: AiSummaryFieldConfigModalProps;
};

export type { DatabaseAiConfigModal };

interface DatabaseAiConfigModalContextValue {
  openAiConfigModal: (modal: DatabaseAiConfigModal) => void;
  closeAiConfigModal: () => void;
}

interface DatabaseAiConfigModalProviderProps {
  children: ReactNode;
}

const DatabaseAiConfigModalContext =
  createContext<DatabaseAiConfigModalContextValue | null>(null);

export const DatabaseAiConfigModalProvider: FunctionComponent<
  DatabaseAiConfigModalProviderProps
> = ({ children }) => {
  const [activeModal, setActiveModal] = useState<DatabaseAiConfigModal | null>(null);
  const isAiFieldsSummaryEnabled = getExperimentValue(
    'databasesFrontendAiFieldsSummary',
    'enabled',
    {
      fireExperimentExposure: false
    }
  );

  const closeAiConfigModal = useCallback(() => {
    setActiveModal(null);
  }, []);

  const value = useMemo(
    () => ({
      openAiConfigModal: setActiveModal,
      closeAiConfigModal
    }),
    [closeAiConfigModal]
  );

  return (
    <DatabaseAiConfigModalContext.Provider value={value}>
      {children}
      {isAiFieldsSummaryEnabled && activeModal?.type === 'ai-summary-field' && (
        <AiSummaryFieldModal
          onClose={() => {
            activeModal.props?.onClose?.();
            closeAiConfigModal();
          }}
          onRegenerate={activeModal.props?.onRegenerate}
          onSaveAndRun={(settings) => {
            activeModal.props?.onSaveAndRun?.(settings);
            closeAiConfigModal();
          }}
          onSaveWithoutRunning={(settings) => {
            activeModal.props?.onSaveWithoutRunning?.(settings);
            closeAiConfigModal();
          }}
        />
      )}
    </DatabaseAiConfigModalContext.Provider>
  );
};

export const useDatabaseAiConfigModal = () => {
  const value = useContext(DatabaseAiConfigModalContext);

  if (!value) {
    throw new Error(
      'useDatabaseAiConfigModal must be used within DatabaseAiConfigModalProvider'
    );
  }

  return value;
};
